import ProductHeading from '@components/Product/Details/_ProductHeading';
import Breadcrumb from '@repo/ui/src/components/Breadcrumb';
import Markdown from '@repo/ui/src/components/Markdown';
import { MediaType } from '@repo/ui/src/types/image';
import { LinkProps } from '@repo/ui/src/types/link';
import { PDPTemplate, isTierOne, isTierTwo } from '@views/ProductDetailPage/_enums';
import Product from '@models/product';
import dynamic from 'next/dynamic';
import React, { useCallback, useContext } from 'react';
import { AlternateColorProps } from '@components/Product/Details/_ProductSelection';
import { ProductContext } from '@context/ProductProvider';
import { ModalID } from '@repo/ui/src/modules/UI';
import { useUI } from '@repo/ui/src/modules/UI/hooks/useUI';
import { BreadcrumbParentProps } from '@repo/ui/src/types/breadcrumb';
import { classNames } from '@repo/ui/src/utils/classNames';

const PDPHeroCarousel = dynamic(() => import('@components/Product/Details/_PDPHeroCarousel'));
const SpinSetWrapper = dynamic(() => import('./_SpinSetWrapper'));
const PromotionsAndPricing = dynamic(
  () => import('@components/Product/Details/_PromotionsAndPricing'),
);
const ProductSelection = dynamic(() => import('@components/Product/Details/_ProductSelection'));
const TierProductSelection = dynamic(
  () => import('@components/Product/Details/_TierProductSelection'),
);

export interface ProductPageProps {
  template: PDPTemplate;
  breadcrumb: BreadcrumbParentProps;
  spinSetUrl?: string;
  product: Product;
  dark?: boolean;
  lifestyleImages?: Array<MediaType>;
  lifestyleVideos?: Array<MediaType>;
  alternateSizes?: {
    sizes: Array<LinkProps>;
  };
  alternateColorsV2?: AlternateColorProps;
  promoMarkdown?: string;
  prop65?: {
    title: string;
    warningBody: string;
  };
  ownerManual?: {
    ownerManualLink: Array<LinkProps>;
  };
  essentials?: Array<{
    id: string;
  }>;
  sizes: Array<Product>;
  customizable?: boolean;
  compatibleWith?: {
    image: MediaType;
    link: LinkProps;
  };
}

const ProductPage = ({
  breadcrumb,
  spinSetUrl,
  product,
  template,
  lifestyleImages = [],
  essentials,
  prop65,
  ownerManual,
  promoMarkdown,
  sizes,
  alternateColorsV2,
  customizable,
  dark,
  compatibleWith,
}: ProductPageProps) => {
  const { productList } = useContext(ProductContext);
  const { pushModal } = useUI();
  const PDPV2 = template === PDPTemplate.PDPV2;
  const NonGrillDark = template === PDPTemplate.NonGrillDark;
  const PDPV2Light = template === PDPTemplate.PDPV2Light;
  const whiteImageBg =
    (template === PDPTemplate.TierOne || template === PDPTemplate.TierTwo) && !dark;
  const darkMode = PDPV2 || NonGrillDark || dark;
  const display =
    productList.filter((product) => product.checked && product.overlayImage).length > 0;
  const encodedSpinSetUrl = spinSetUrl ? encodeURI(spinSetUrl) : null;

  const openGalleryDialog = useCallback(
    (images: any, padding: any) => {
      pushModal(ModalID.gallery, {
        images,
        useImagePadding: padding,
      });
    },
    [pushModal],
  );

  const hasSpinSet = encodedSpinSetUrl && product.transparentImages;
  const isNewTemplate =
    isTierOne(template) || isTierTwo(template) || template === PDPTemplate.TierThree;

  const showPrice =
    ![PDPTemplate.PDPV2, PDPTemplate.PDPV2Light, PDPTemplate.TierOne].includes(template) &&
    product?.ctaButtonType !== 'noCTA';

  return (
    <div
      className={classNames(
        'sm:tw-relative tw-items-start',
        darkMode ? 'tw-bg-neutral-800' : 'tw-bg-white',
      )}
    >
      {breadcrumb && (
        <Breadcrumb {...breadcrumb} dark={darkMode} title={product.title} className="tw-z-50" />
      )}
      <div className="tw-grid tw-gap-y-6 md:tw-grid-cols-[1fr_calc(554px+8vw)] tw-items-start tw-auto-cols-fr">
        <div
          className={classNames(
            'md:tw-sticky tw-top-[45px] tw-grid tw-gap-y-6 md:tw-grid-flow-col',
            !hasSpinSet && 'md:tw-pt-[120px] md:tw-pb-16',
            whiteImageBg && 'tw-bg-white',
            PDPV2Light && 'tw-bg-warm-100',
          )}
        >
          <div
            className={classNames(
              'tw-pb-8 md:tw-pb-0 md:tw-pt-8 md:tw-hidden tw-grid tw-gap-y-2 tw-pt-5',
              isNewTemplate && !dark && 'tw-bg-warm-100',
            )}
          >
            <ProductHeading
              product={product}
              className={classNames('md:tw-relative', PDPV2Light && 'tw-bg-warm-100')}
              dark={darkMode}
              hideDescription
              hideSKU={template === PDPTemplate.TierThree}
            />
            {showPrice && (
              <PromotionsAndPricing
                promotions={product?.promotions}
                customizable={customizable}
                product={product}
                dark={darkMode}
                className="tw-flex tw-flex-wrap tw-px-6 tw-justify-center"
                isNewTemplate={isNewTemplate}
              />
            )}
          </div>
          <div
            className={classNames(
              'tw-z-10 tw-w-full tw-flex tw-flex-col tw-gap-10',
              PDPV2Light && 'tw-bg-warm-100',
            )}
          >
            <div className="tw-flex tw-flex-col tw-h-full tw-w-full tw-z-0 tw-gap-y-6 tw-items-center tw-justify-center">
              <div
                className={classNames(
                  'tw-relative tw-w-full tw-z-10 tw-flex tw-items-center tw-justify-stretch tw-bg-center tw-bg-contain tw-bg-no-repeat',
                  darkMode &&
                    (template === PDPTemplate.PDPV2 || isTierOne(template)) &&
                    'tw-bg-corrugatedMetal',
                  hasSpinSet && 'md:tw-h-[calc(100vh_-_76px)]',
                )}
                data-testID="image-wrapper"
              >
                {hasSpinSet ? (
                  <SpinSetWrapper
                    product={product}
                    productList={productList}
                    display={display}
                    encodedSpinSetUrl={encodedSpinSetUrl}
                    images={product.transparentImages!}
                    dark={darkMode}
                  />
                ) : (
                  <PDPHeroCarousel
                    images={[
                      ...(product.images ?? []),
                      ...(product.transparentImages ?? []),
                      ...(lifestyleImages ?? []),
                    ]}
                    openGalleryDialog={openGalleryDialog}
                    dark={darkMode}
                  />
                )}
              </div>
              {product.shortDescription && (
                <div
                  className={classNames(
                    'tw-text-left md:tw-hidden tw-px-6 tw-text-inherit',
                    'md:tw-bg-transparent',
                  )}
                >
                  <Markdown bodySize="large" className={classNames(darkMode && 'tw-text-white')}>
                    {product.shortDescription}
                  </Markdown>
                </div>
              )}
            </div>
          </div>
        </div>
        <div
          className={classNames(
            'lg:tw-pr-16 md:tw-pl-[3vw] md:tw-min-h-full',
            darkMode && 'tw-bg-neutral-800',
            PDPV2Light && 'tw-bg-warm-200',
            !darkMode && !PDPV2Light && 'tw-bg-warm-100',
          )}
        >
          <div className="tw-px-6 md:tw-px-0 md:tw-mr-4 md:tw-pb-15 md:tw-sticky tw-top-0 tw-self-start tw-py-6 md:tw-py-20">
            {isNewTemplate ? (
              <TierProductSelection
                template={template}
                dark={darkMode}
                product={product}
                essentials={essentials}
                prop65={prop65}
                promoMarkdown={promoMarkdown}
                alternateColors={alternateColorsV2}
                ownerManual={ownerManual}
                alternateSize={sizes?.[0]}
                customizable={customizable}
                compatibleWith={compatibleWith}
              />
            ) : (
              <ProductSelection
                dark={darkMode}
                product={product}
                essentials={essentials}
                prop65={prop65}
                promoMarkdown={promoMarkdown}
                alternateColors={alternateColorsV2}
                ownerManual={ownerManual}
                alternateSize={sizes?.[0]}
                template={template}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
